import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { delay, retryWhen } from 'rxjs/operators';
import { IRun } from '../../core/model/runs/run';
import { EDITABLE_RUN_STATUSES } from '../../core/model/runs/run-status';
import { IApiResponseWrapper } from '../../core/model/v2-api/v2-api-wrappers';
import { genericRetryWhen, observableEmitDelay } from '../../core/rxjsutils/rxjs-utilities';
import { BsApiEndPoints } from '../../core/services/bs-api/endpoints';
import { ErrorMessages } from '../../core/utilities/error-messages';

export interface IRunEditService {
  canEditRun(run: IRun, currentUserId: string): boolean;
  editRunName(newExperimentName: string, run: IRun, currentUserId: string): Observable<IApiResponseWrapper<IRun>>;
}

@Injectable({
  providedIn: 'root'
})
export class RunEditService implements IRunEditService{

  constructor( private httpClient: HttpClient ) { }

  /* Given a Run and User, return whether the run is editable.  */
  canEditRun(run: IRun, currentUserId: string): boolean {
    if (!run || !currentUserId) {
      return false;
    }

    const isRunStatusEditable = EDITABLE_RUN_STATUSES.includes(run.Status);
    return !!(isRunStatusEditable && this.isRunOwner(currentUserId, run));
  }

  editRunName(newExperimentName: string, run: IRun, currentUserId: string): Observable<IApiResponseWrapper<IRun>> {
    // Sadly, v2 post-runs-id doesn't support the UI's only use case: experimentName editing :(
    // note: have to use v1Pre3Id throughout to call v1 APIs

    if (!run) {
      return throwError(ErrorMessages.RUN_EDIT.NO_RUN_SPECIFIED);
    }

    if (!currentUserId) {
      return throwError(ErrorMessages.RUN_EDIT.NO_USER_SPECIFIED);
    }

    if (!this.isRunOwner(currentUserId, run)) {
     return throwError(ErrorMessages.RUN_EDIT.NOT_OWNER);
    }

    const requestUrl = BsApiEndPoints.getRunInfoEditUrl(run.V1Pre3Id);
    const postData: any = { Id: run.V1Pre3Id, ExperimentName: newExperimentName };

    return this.httpClient.post<IApiResponseWrapper<IRun>>(requestUrl, postData, this.v1ApiRequestOptions()).pipe(
      // Retry in case of HTTP errors
      retryWhen(genericRetryWhen()),
      // To avoid a Flash of content, maintain a delay
      delay(observableEmitDelay),
    );
  }

  private isRunOwner(currentUserId: string, run: IRun) {
    return run.UserOwnedBy.Id === currentUserId;
  }

    /* Builds Request for calling v1 APIs */
  // tslint:disable-next-line: ban-types
  private v1ApiRequestOptions(): Object {
    const headerArray = new HttpHeaders({ 'Content-Type': 'application/json; charset=utf-8' });
    return {
      headers: headerArray,
    };
  }
}
